import { useState } from 'react';
import { Check, PhoneMissed, Play, Voicemail } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTelephonyStore } from '../../store/telephonyStore';
import { formatPhoneForDisplay } from '../../lib/telephony/dialer';
import type { MissedCall } from '../../hooks/useMissedCalls';
import { CallButton } from './CallButton';

interface MissedCallRowProps {
  call: MissedCall;
  onHandled: (callerE164: string) => void;
}

/** One caller to call back: who, when, how many times, the message left, and the two ways to call. */
export function MissedCallRow({ call, onHandled }: MissedCallRowProps) {
  const getRecordingUrl = useTelephonyStore((s) => s.getRecordingUrl);
  const [open, setOpen] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);

  const number = formatPhoneForDisplay(call.caller_e164);
  const hasVoicemail = call.voicemail_call_id !== null;
  const voicemailId = call.voicemail_call_id;

  const loadAudio = async () => {
    if (!voicemailId || audioUrl || loadingAudio) return;
    setLoadingAudio(true);
    setAudioUrl(await getRecordingUrl(voicemailId));
    setLoadingAudio(false);
  };

  return (
    <li className="px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        {hasVoicemail ? (
          <Voicemail className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" aria-label="Message vocal" />
        ) : (
          <PhoneMissed className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" aria-label="Appel manqué" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-neutral-900 dark:text-gray-100">
              {call.contact_name ?? number}
            </span>
            {call.missed_count > 1 && (
              <span className="flex-shrink-0 rounded-full bg-red-50 px-1.5 text-[10px] font-semibold text-red-600 dark:bg-red-950 dark:text-red-400">
                ×{call.missed_count}
              </span>
            )}
          </div>
          <p className="truncate text-xs text-neutral-500 dark:text-gray-400">
            {[call.account_name, call.contact_name ? number : null].filter(Boolean).join(' · ') || 'Numéro inconnu du CRM'}
          </p>
          <p className="text-[11px] text-neutral-400">
            {formatDistanceToNow(new Date(call.last_missed_at), { addSuffix: true, locale: fr })}
            {hasVoicemail && (
              <>
                {' · '}
                <button
                  type="button"
                  onClick={() => setOpen((o) => !o)}
                  className="font-medium text-amber-600 hover:underline dark:text-amber-400"
                >
                  {open ? 'Masquer le message' : 'Voir le message'}
                </button>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onHandled(call.caller_e164)}
          title="Marquer comme traité sans rappeler"
          aria-label="Marquer comme traité"
          className="flex-shrink-0 rounded-md p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-green-600 dark:hover:bg-gray-800"
        >
          <Check className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <div className="ml-6 mt-2 space-y-2 rounded-lg bg-neutral-50 p-2 dark:bg-gray-800/60">
          {audioUrl ? (
            <audio controls src={audioUrl} className="h-8 w-full" />
          ) : (
            <button
              type="button"
              onClick={() => void loadAudio()}
              disabled={loadingAudio}
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-2 py-1 text-xs font-medium text-neutral-700 shadow-sm hover:bg-neutral-100 disabled:opacity-50 dark:bg-gray-900 dark:text-gray-200"
            >
              <Play className="h-3.5 w-3.5" />
              {loadingAudio ? 'Chargement…' : 'Écouter'}
            </button>
          )}
          <p className="whitespace-pre-wrap text-xs leading-relaxed text-neutral-600 dark:text-gray-300" data-sensitive-text="">
            {call.voicemail_transcript ?? 'Pas de transcription.'}
          </p>
        </div>
      )}

      <div className="ml-6 mt-1.5">
        <CallButton
          number={call.caller_e164}
          contactId={call.contact_id}
          accountId={call.account_id}
          label="Rappeler"
        />
      </div>
    </li>
  );
}
